// START IMPORTS
// Context and configuration imports
import { AppConfig } from '@ltvco/refresh-lib/ctx';
// Hooks imports
import { usePhoneSearchData, useReport } from '@ltvco/refresh-lib/hooks';
import { Box, Stack, useTheme } from '@ltvco/refresh-lib/theme';
import {
  DateUtil,
  isNeighborSpoofed,
  isZeroed,
  unformatPhone,
  usePhoneFormatter,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
import {
  ReportLoading,
  type ReportOptions,
  type commonTypes,
  type ReportMonitor,
  DebugMenu,
} from '@ltvco/refresh-lib/v1';
import {
  ReportFactory,
  AddressHistorySection,
  AssetsSection,
  CommentsSection,
  EducationSection,
  EmailSection,
  FraudPhoneThreatDetection,
  JobsSection,
  NotesSection,
  PhoneSection,
  PotentialOwners,
  RelatedReportsSection,
  ReportActionsWithDateUpdated,
  ReportChangesOverview,
  ReportNavigationMenu,
  type ReportNavigationMenuProps,
  ReportNavigationMobileMenu,
  ReportNullState,
  ReportRating,
  SocialSection,
  PhoneReportOverview,
  ReportClaimingSection,
  MonitoringCardV2,
  MonitoringBannerSection,
  usePhoneComments,
  NavLinkDataV2,
  useSession,
} from '@ltvco/refresh-lib/v2';
// Theme and UI imports
// Utility imports
// Core lib v1 imports
// Core lib v2 imports
// Vendor imports
import { useQueryClient, useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import { getPhoneNavLinkDataV2 } from 'navLinkData/phoneNavLinkData';

import { getReportSections } from './utils/getReportSections';

// Asset imports
// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface PhoneReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'phone';
// END CONSTANTS

export function PhoneReportPage({
  permalink,
  isMonitored: _unusedInitialMonitored = false,
}: PhoneReportProps) {
  // START HOOKS_AND_QUERIES
  const {
    session: { account },
  } = useSession();
  const { logError } = useContext(AppConfig);
  const queryClient = useQueryClient();
  const queryResult = useReport(permalink);
  const { currentMonitors, reportIsMonitored } = queryResult;
  const { hash, search } = useLocation();
  const currentUserInfo = account?.account?.user_info;

  const { data: phoneSearchData } = usePhoneSearchData(
    queryResult?.data?.meta?.search_data?.phone_number ?? ''
  );

  const rawPhone = queryResult?.data?.meta?.search_data?.phone_number ?? '';
  const formattedPhoneNumber = usePhoneFormatter(rawPhone, true);

  const { commentsQuery } = usePhoneComments({
    permalink,
    phoneNumber: rawPhone,
    logError,
    enabled: queryResult.isSuccess,
  });

  const isPhonePartialAddress = useFeatureIsOn('OAR_1707');

  const report = useMemo(() => {
    if (!queryResult.isSuccess || !queryResult.data) {
      return null;
    }

    if (queryResult?.data?.entities) {
      return ReportFactory.create(queryResult?.data, 'phone');
    }

    return null;
  }, [queryResult.data, queryResult.isSuccess]);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [spam, setSpam] = useState<'unsure' | 'yes' | 'no'>('unsure');
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: false,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });
  const [activeMonitor] = useState(false);
  const [activeClaiming, setActiveClaiming] = useState(false);
  // END STATE_MANAGEMENT

  // START DERIVED_DATA_AND_UTILS
  const zeroed = isZeroed(queryResult);
  useScrollToSectionOnNavigate(hash, queryResult);
  // END DERIVED_DATA_AND_UTILS

  // START EVENT_HANDLERS
  const handleSetReportOptions = (newOptions: Partial<ReportOptions>) => {
    setReportOptions((prev) => ({ ...prev, ...newOptions }));
  };
  // END EVENT_HANDLERS

  // START EFFECTS
  useEffect(() => {
    setActiveClaiming(
      currentMonitors?.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [currentMonitors, permalink]);

  useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      queryClient.invalidateQueries(['request', 'bbq']);
    }
  }, [queryResult.isSuccess, queryResult.data, reportOptions]);
  // END EFFECTS

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={15} />;
  }
  if (zeroed || (queryResult.isSuccess && !report)) {
    return <ReportNullState />;
  }

  if (!report) {
    return <ReportLoading menuItems={15} />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const businesses = (
    report?.data?.businesses ??
    report?.data?.rawData?.entities?.businesses ??
    []
  ).filter((b) => Boolean(b.names?.[0]?.name));
  const businessName = businesses[0]?.names?.[0]?.name ?? '';

  const showReportClaiming =
    unformatPhone(currentUserInfo?.phone_number || '') === report.phone;

  const ClaimingComponent = showReportClaiming ? (
    <ReportClaimingSection
      reportPermaLink={permalink}
      reportType={reportType}
      reportIsMonitoredAndClaimed={activeClaiming}
    />
  ) : reportIsMonitored ? null : (
    <MonitoringBannerSection
      reportPermaLink={permalink}
      reportType={reportType}
    />
  );

  const MonitoringComponent = (
    <MonitoringCardV2 permalink={permalink} reportType={reportType} />
  );

  const neighborSpoofed = isNeighborSpoofed(
    report.data,
    account?.account?.user_info.phone_number
  );

  const isBusinessSelected =
    reportOptions.potentialOwnerIndex < businesses.length;
  const selectedBusiness = isBusinessSelected
    ? businesses[reportOptions.potentialOwnerIndex]
    : undefined;
  const selectedBusinessName = selectedBusiness?.names?.[0]?.name ?? '';

  const selectedPersonIndex = isBusinessSelected
    ? 0
    : reportOptions.potentialOwnerIndex - businesses.length;
  const personOwner = report.getOwner({
    ...reportOptions,
    potentialOwnerIndex: selectedPersonIndex,
  });

  const owner = selectedBusiness
    ? {
        ...personOwner,
        ownerName: selectedBusinessName,
        emails: selectedBusiness.emails ?? [],
        phones: (selectedBusiness.phones ?? []).map((phone) => ({
          ...phone,
          phoneType: phone.phoneType || phone.type || undefined,
        })),
        addresses: selectedBusiness.addresses ?? [],
        educations: [],
        jobs: [],
        profiles: [],
        usernames: [],
        userIds: [],
        relatives: [],
        neighbors: [],
        associates: [],
        ownedAssets: {
          vehicles: { aircraft: [], automobiles: [], watercraft: [] },
          real_estate: [],
        },
      }
    : personOwner;

  const selectedOwnerSectionKey = `owner-${reportOptions.potentialOwnerIndex}`;
  const selectedPerson = isBusinessSelected
    ? undefined
    : report.data.people?.[selectedPersonIndex];

  const {
    ownerName,
    emails,
    educations,
    jobs,
    profiles,
    usernames,
    phones,
    addresses,
    ownedAssets,
    relatives,
  } = owner;

  const {
    risk,
    connectivity,
    location: supportingLocation,
  } = report.supportingPhone || {};

  const {
    fraud_score,
    is_risky,
    is_recent_abuse,
    is_spammer,
    is_do_not_call,
    is_voip,
    is_prepaid,
    is_leaked,
  } = risk || {};

  const { active_status, user_activity, is_active } = connectivity || {};

  const queryParams = new URLSearchParams(search);
  const fallbackPhone = queryParams.get('phone') || '';

  const otherPhones = isBusinessSelected
    ? phones
    : phones.filter(
        (phone: commonTypes.Phone) =>
          phone.number !== fallbackPhone && phone.number !== report.phone
      );

  const selectedOwnerDisplayName = isBusinessSelected
    ? selectedBusinessName
    : ownerName || formattedPhoneNumber;

  let potentialOwnersNumber =
    businesses.length + (report.data.people?.length ?? 0);
  if (potentialOwnersNumber === 0 && businessName) {
    potentialOwnersNumber = 1;
  }

  const people = report.data.people ?? [];
  const businessNames = businesses.map(
    (b) => `${b.names?.[0]?.name} (Business)`
  );
  const personNames = people.map((person) => {
    const first = person?.identity?.names?.[0]?.parsed?.first ?? '';
    const last = person?.identity?.names?.[0]?.parsed?.last ?? '';
    return `${first} ${last}`.trim() || 'Unknown';
  });
  const potentialOwnersNames = [...businessNames, ...personNames];

  const { main_line_type, num_searches, spam_score } = report.data;
  const searchCount = phoneSearchData?.total_volume || num_searches || 0;
  const comments = commentsQuery.isSuccess ? commentsQuery.data.comments : [];
  const commentsCount = commentsQuery.isSuccess
    ? Number(commentsQuery.data.comment_count ?? comments.length)
    : 0;
  const internationalPhoneNumber = (rawPhone as string).startsWith('+');
  const supportingPhone = queryResult?.data?.supporting?.phones?.[0];
  const fallbackInfo = internationalPhoneNumber
    ? {
        address: supportingPhone?.location?.address || undefined,
        phone: formattedPhoneNumber || undefined,
      }
    : undefined;

  const Theme = useTheme();
  const phoneNavLinkDataV2 = getPhoneNavLinkDataV2(
    owner,
    {
      potentialOwnersLength: potentialOwnersNumber,
      searchVolumeLength: searchCount > 0 ? 1 : 0,
      commentsLength: commentsCount,
      neighborSpoofingLength: neighborSpoofed ? 1 : 0,
      spamReportsCount: report.data.spam_reports_count ?? 0,
    },
    Theme
  ) as unknown as NavLinkDataV2[];

  const carrier = report.data.meta?.search_data?.carrier;
  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';

  document.title = `${formattedPhoneNumber} - NumberGuru`;
  const alternativeTitle = isPhonePartialAddress
    ? 'Possible Locations'
    : undefined;
  const phoneOverviewItemsData = {
    comments,
    commentsCount,
    mainLineType: main_line_type,
    numSearches: searchCount,
    spamScore: spam_score,
    carrier,
    peopleCount: potentialOwnersNumber,
    fraudScore: fraud_score ?? 0,
    isRisky: is_risky,
    isDoNotCall: is_do_not_call ?? false,
    isVOIP: is_voip ?? false,
    isPrepaid: is_prepaid ?? false,
    isValid: report.supportingPhone?.is_valid ?? false,
    isActive: is_active ?? false,
    isLeaked: is_leaked ?? false,
    isSpammer: is_spammer ?? false,
    isRecentAbuse: is_recent_abuse ?? false,
    activeStatus: active_status ?? 'Unknown',
    userActivity: user_activity ?? 'Unknown',
    timezone: supportingLocation?.timezone ?? '',
    potentialOwners: potentialOwnersNames,
    parsedLocation: supportingLocation?.address?.parsed,
    location: supportingLocation?.address?.full ?? '',
  };

  const reportSections = getReportSections(phoneNavLinkDataV2);
  // END DATA_PREPARATION

  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Phone Report"
            headerTitle={formattedPhoneNumber}
            menuItems={
              phoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        <Box marginTop={0}>
          {activeMonitor && (
            <ReportChangesOverview
              permalink={permalink}
              navLinkData={phoneNavLinkDataV2}
            />
          )}

          <Box
            sx={{
              height: 30,
              backgroundColor: 'background.default',
              position: 'sticky',
              marginBottom: '-10px',
              marginX: -1,
              top: 52,
              zIndex: 10,
              display: {
                xs: 'none',
                sm: 'block',
                md: 'block',
                lg: 'block',
              },
            }}
          />

          <ReportActionsWithDateUpdated
            reportType={reportType}
            reportTitle="Phone"
            dateUpdated={reportUpdateDate}
            reportUpgraded={false}
          />

          <PhoneReportOverview
            dateUpdated={reportUpdateDate}
            reportType={reportType}
            phone={formattedPhoneNumber}
            personName={selectedOwnerDisplayName}
            neighborSpoofed={neighborSpoofed}
            Monitoring={MonitoringComponent}
            phoneOverviewItemsData={phoneOverviewItemsData}
            showHighConfidenceToggle={false}
            showHighConfidenceDataOnly={
              reportOptions.showHighConfidenceDataOnly
            }
            setShowHighConfidenceDataOnly={(value: boolean) => {
              handleSetReportOptions({ showHighConfidenceDataOnly: value });
            }}
            useThreatDetectionSection
            internationalPhoneNumber={internationalPhoneNumber}
            potentialOwner={selectedPerson}
            fallbackInfo={fallbackInfo}
          />

          {ClaimingComponent}

          <PotentialOwners
            potentialOwners={people}
            potentialOwnerIndex={reportOptions.potentialOwnerIndex}
            setPotentialOwnerIndex={(index: number) => {
              handleSetReportOptions({ potentialOwnerIndex: index });
            }}
            personName={selectedOwnerDisplayName}
            permalink={permalink}
            businesses={businesses}
            reportType={reportType}
            reportSections={reportSections}
          />
          <Fragment key={selectedOwnerSectionKey}>
            <PhoneSection
              personName={selectedOwnerDisplayName}
              phoneList={otherPhones}
              permalink={permalink}
              alternateTitle="Other Phone Numbers"
            />
            <EmailSection
              personName={selectedOwnerDisplayName}
              emailList={emails}
              permalink={permalink}
            />
            <AddressHistorySection
              addresses={addresses}
              personName={selectedOwnerDisplayName}
              permalink={permalink}
              alternateTitle={alternativeTitle}
            />
            <SocialSection
              personName={selectedOwnerDisplayName}
              profiles={profiles}
              usernames={usernames}
              permalink={permalink}
            />
            <JobsSection
              personName={selectedOwnerDisplayName}
              jobs={jobs}
              permalink={permalink}
            />

            <EducationSection
              personName={selectedOwnerDisplayName}
              educations={educations}
              permalink={permalink}
            />
            <AssetsSection
              personName={selectedOwnerDisplayName}
              ownedAssets={ownedAssets}
              permalink={permalink}
            />
          </Fragment>

          <FraudPhoneThreatDetection
            isOnPhoneReport
            fraudScore={fraud_score ?? 0}
            isRisky={is_risky}
            isRecentAbuse={is_recent_abuse}
            isSpammer={is_spammer}
            searchCount={searchCount}
            spamScore={report?.data?.spam_score}
            spamReportsCount={report?.data?.spam_reports_count}
            userComments={comments?.length}
            permalink={permalink}
            spam={spam}
            setSpam={setSpam}
          />

          <CommentsSection
            commentsCount={commentsCount}
            comments={commentsQuery.data?.comments ?? []}
            reportData={report.data}
            permalink={permalink}
            formattedPhoneNumber={formattedPhoneNumber}
            reportType={reportType}
          />

          <NotesSection permalink={permalink} />

          <RelatedReportsSection
            key={`related-${selectedOwnerSectionKey}`}
            reportType="phone"
            personName={selectedOwnerDisplayName}
            potentialOwners={people}
            relatives={relatives}
            addresses={addresses}
            phones={phones}
            emails={emails}
            usernames={usernames}
          />
          <ReportRating
            rating={report.data.meta?.rating || null}
            report_type={reportType}
          />
        </Box>
      </Stack>
      <ReportNavigationMobileMenu
        reportType="Phone Report"
        headerTitle={formattedPhoneNumber}
        menuItems={phoneNavLinkDataV2 as ReportNavigationMenuProps['menuItems']}
        isMobileBannerShowed={false}
      />
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
    </Box>
  );
  // END RENDER
}
